import React, { useState, useEffect, useRef } from "react";
import styles from "./Payment.module.scss";
import { useLoaderData } from "react-router";
import Order from "@models/order";
import { storesAPI } from "@api/main";
import { globalPopupStore, authStore } from "@stores/main";
import { rendermixAPI } from "@api/main";
import { usePageTitle } from '../../hooks/usePageTitle';
import { pageTrackingService } from '../../services/pageTracking';
import { PaymentMethodsResponse } from "@models/payment-method";
import { observer } from "mobx-react";
import Loader from "@components/Loader/Loader";
import { gtagService } from '../../services/gtag';
import { FaCreditCard, FaWallet, FaBuilding } from 'react-icons/fa';
import { HiCreditCard } from 'react-icons/hi';
import { IconType } from 'react-icons';
import Modal from "@components/Modal/Modal";
import BillingAddressForm from "@components/BillingAddressForm/BillingAddressForm";
import { BillingAddressData } from "@models/billing-address";
import { hasStates } from "@utils/states";
import modalStyles from "./BillingAddressModal.module.scss";

// Helper function to render payment icon using professional finance icons
const PaymentIcon = ({ methodKey }: { methodKey: string }) => {
  // Use UPI logo SVG for UPI payment method
  if (methodKey === 'upi') {
    return (
      <img 
        src={`${process.env.PUBLIC_URL}/images/icons/upi-logo.svg`}
        alt="UPI"
        style={{ width: '24px', height: '24px', objectFit: 'contain' }}
      />
    );
  }

  const iconMap: { [key: string]: IconType } = {
    card: FaCreditCard,
    emi: HiCreditCard,
    netbanking: FaBuilding,
    wallet: FaWallet
  };
  
  const IconComponent = iconMap[methodKey] || iconMap.card;
  if (!IconComponent) return null;
  
  // Type assertion to work with React 19 and react-icons
  const Component = IconComponent as React.ComponentType<{ size?: number }>;
  return React.createElement(Component, { size: 24 });
};

const Payment: React.FC = () => {
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMethods, setIsLoadingMethods] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsResponse>({});
  const [showBillingAddressModal, setShowBillingAddressModal] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState<string | null>(null);
  const messageIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const orderSummaryRef = useRef<HTMLDivElement | null>(null);
  const order = useLoaderData<Order>();

  // Engaging messages to keep users hooked during payment processing
  const loadingMessages = [
    "Redirecting to payment gateway..."
  ];

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
      }
    };
  }, []);


  const moreThanOneQuantity = order.items.some(item => item.quantity > 1);

  // Track checkout view and begin checkout conversion
  useEffect(() => {
    pageTrackingService.trackCheckoutView(order.orderSessionId, order.totalItemsBuyPriceString);
    
    // Track begin checkout conversion
    gtagService.trackBeginCheckout({
      currency: order.currency || 'USD',
      value: order.totalItemsBuyPrice,
      items: order.items.map(item => ({
        item_id: item.id,
        item_name: item.title,
        price: item.buyPrice,
        quantity: item.quantity
      }))
    });
  }, [order.orderSessionId, order.totalItemsBuyPriceString, order.currency, order.totalItemsBuyPrice, order.items]);

  useEffect(() => {
    if (order.globalMessage) {
      globalPopupStore.show(order.globalMessage);
    }
  }, [order.globalMessage]);

  useEffect(() => {
    // Load payment methods immediately when order is available
    // Auth is handled by API interceptor, no need to wait for authStore.user
    if (order.orderSessionId) {
      setIsLoadingMethods(true);
      storesAPI
        .getPayemntMethds(order.orderSessionId)
        .then((methods) => {
          setPaymentMethods(methods);
          // Auto-select first enabled method according to predefined order
          const paymentMethodOrder = ['upi', 'card', 'netbanking', 'wallet'];
          const enabledMethods = Object.keys(methods).filter(key => methods[key].enabled);
          // Sort enabled methods by predefined order
          const sortedMethods = enabledMethods.sort((a, b) => {
            const indexA = paymentMethodOrder.indexOf(a);
            const indexB = paymentMethodOrder.indexOf(b);
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
          if (sortedMethods.length > 0) {
            setSelectedPayment(prev => prev || sortedMethods[0]);
          }
        })
        .catch((error) => {
          console.error('Error loading payment methods:', error);
          globalPopupStore.show('Failed to load payment methods. Please try again.');
        })
        .finally(() => {
          setIsLoadingMethods(false);
        });
    }
  }, [order.orderSessionId]);

  usePageTitle({
    title: 'Payment | OnePlay Store'
  });

  // Check if device is mobile
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  const handlePaymentSelect = (id: string) => {
    setSelectedPayment(id);
    pageTrackingService.trackPaymentMethodSelect(id);
    
    // Scroll to order summary on mobile with navbar offset
    if (isMobile() && orderSummaryRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          const element = orderSummaryRef.current;
          if (element) {
            // Use scrollIntoView with scroll-margin-top (already set in CSS)
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 100);
      });
    }
  };


  // Helper function to validate if billing address is complete
  const isBillingAddressComplete = (): boolean => {
    const user = authStore.user;
    if (!user) {
      return false;
    }

    const address = user.billingAddress;
    if (!address) {
      return false;
    }

    // Check all required fields are present and non-empty
    // Don't rely on hasBillingAddress flag - always validate all fields
    const requiredFields: Array<keyof typeof address> = ["name", "country", "city", "street_address", "pin_code"];
    
    for (const field of requiredFields) {
      if (!address[field] || String(address[field]).trim() === "") {
        return false;
      }
    }

    // Check state field - required if country has states defined
    if (address.country) {
      if (hasStates(address.country)) {
        // State is required for this country
        if (!address.state || String(address.state).trim() === "") {
          return false;
        }
      }
      // If country doesn't have states, state field is optional (can be empty or undefined)
    }

    return true;
  };

  const handleProceed = async (method: string) => {
    if (order.globalMessage) {
      globalPopupStore.show(order.globalMessage);
      return;
    }

    // Fetch latest user profile to ensure billing address status is up-to-date
    await authStore.fetchUser();

    // If has_billing_address is true, skip the billing address check completely
    if (authStore.user?.hasBillingAddress) {
      await proceedToPayment(method);
      return;
    }

    // Check if user has complete billing address (only when has_billing_address is false)
    if (!isBillingAddressComplete()) {
      setPendingPaymentMethod(method);
      setShowBillingAddressModal(true);
      return;
    }

    await proceedToPayment(method);
  };

  const proceedToPayment = async (method: string) => {
    // Initialize loading state and messages only on first call
    if (!isLoading) {
      setIsLoading(true);
      // Start with first message
      setLoadingMessage(loadingMessages[0]);
      
      // Rotate messages every 5 seconds to keep users engaged
      let messageIndex = 0;
      messageIntervalRef.current = setInterval(() => {
        messageIndex = (messageIndex + 1) % loadingMessages.length;
        setLoadingMessage(loadingMessages[messageIndex]);
      }, 5000);
    }
    
    // Extract first provider from the selected payment method's providers array
    const selectedMethod = paymentMethods[method];
    const provider = selectedMethod && selectedMethod.providers && selectedMethod.providers.length > 0 
      ? selectedMethod.providers[0] 
      : method; // Fallback to method if no providers available
    
    try {
      const { session: res } = await storesAPI.createPaySession(order.orderSessionId, provider, method);
      if (res.globalMessage) {
        globalPopupStore.show(res.globalMessage);
        setIsLoading(false);
        if (messageIntervalRef.current) {
          clearInterval(messageIntervalRef.current);
          messageIntervalRef.current = null;
        }
        return;
      }
      switch (res.state) {
        case "wait":
          // Keep loading state active and continue rotating messages
          // Retry after 5 seconds
          setTimeout(() => proceedToPayment(method), 5000);
          break;
        case "refresh":
          if (messageIntervalRef.current) {
            clearInterval(messageIntervalRef.current);
            messageIntervalRef.current = null;
          }
          window.location.reload();
          break;
        case "redirect":
          if (messageIntervalRef.current) {
            clearInterval(messageIntervalRef.current);
            messageIntervalRef.current = null;
          }
          // Gateway handoff — NOT a confirmed purchase; completion is
          // recorded on the /orders?order_id= return.
          pageTrackingService.trackPaymentInitiated(order.orderSessionId, order.totalItemsBuyPriceString, method);
          window.location.href = res.redirectPaymentUrl;
          break;
        case "failed":
          pageTrackingService.trackPaymentFailed(order.orderSessionId, method, "session_failed");
          setIsLoading(false);
          if (messageIntervalRef.current) {
            clearInterval(messageIntervalRef.current);
            messageIntervalRef.current = null;
          }
          break;
      }
    } catch (error) {
      setIsLoading(false);
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
      // Show error message to user
      globalPopupStore.show('Payment session creation failed. Please try again.');
    }
  };

  const handleBillingAddressSubmit = async (data: BillingAddressData) => {
    try {
      await rendermixAPI.updateBillingAddress(data);
      // Refresh user data
      await authStore.fetchUser();
      setShowBillingAddressModal(false);
      globalPopupStore.show('Billing address saved successfully!');
      
      // Proceed with payment if there's a pending payment method
      if (pendingPaymentMethod) {
        setPendingPaymentMethod(null);
        await proceedToPayment(pendingPaymentMethod);
      }
    } catch (error) {
      console.error('Error saving billing address:', error);
      globalPopupStore.show('Failed to save billing address. Please try again.');
    }
  };

  const goToCartPage = async () => {
    console.error('flutterChannel is not defined.');
    const cartUrl = process.env.REACT_APP_STORE_URL + '/cart';
    if (cartUrl) {
      window.location.href = cartUrl;
    } else {
      console.error('Login page URL is not defined');
    }
  };


  // Calculate convenience fee for selected payment method
  const getConvenienceFee = (): number => {
    if (!selectedPayment || !paymentMethods[selectedPayment]) return 0;
    const fee = paymentMethods[selectedPayment].convenience_fee;
    if (fee > 0) {
      return (order.totalItemsBuyPrice * fee) / 100;
    }
    return 0;
  };

  // Format currency based on order currency
  const formatCurrency = (amount: number): string => {
    const currency = order.currency || 'INR';
    const currencySymbols: { [key: string]: string } = {
      'INR': '₹',
      'USD': '$',
      'EUR': '€',
      'GBP': '£'
    };
    const symbol = currencySymbols[currency] || currency;
    return `${symbol}${amount.toFixed(2)}`;
  };

  // Define the order for payment methods
  const paymentMethodOrder = ['upi', 'card', 'netbanking', 'wallet'];

  // Sort payment methods according to the defined order
  const getSortedPaymentMethods = () => {
    const enabledMethods = Object.entries(paymentMethods)
      .filter(([key, method]) => method.enabled);
    
    // Sort by the predefined order
    return enabledMethods.sort(([keyA], [keyB]) => {
      const indexA = paymentMethodOrder.indexOf(keyA);
      const indexB = paymentMethodOrder.indexOf(keyB);
      
      // If method is not in the order array, put it at the end
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      
      return indexA - indexB;
    });
  };


  return (
    <div className={styles.paymentPage}>
      <main className={styles.mainContent}>
        <div className={styles.paymentContainer}>
          {/* Left Panel - Payment Methods */}
          <div className={styles.paymentMethodsPanel}>
            <div className={styles.panelHeader}>
              <img
                src="/images/icons/back-arrow.svg"
                alt="Back"
                onClick={goToCartPage}
                className={styles.backButton}
              />
            </div>

            {isLoadingMethods ? (
              <div className={styles.loadingMethods}>
                <Loader size={40} color="#A83AFE" />
                <span>Loading payment methods...</span>
              </div>
            ) : Object.keys(paymentMethods).filter(key => paymentMethods[key].enabled).length > 0 ? (
              <div className={styles.paymentMethodsList}>
                {getSortedPaymentMethods()
                  .map(([methodKey, method]) => (
                    <div 
                      key={methodKey} 
                      className={`${styles.paymentMethodItem} ${
                        selectedPayment === methodKey ? styles.methodItemActive : ""
                      }`}
                      onClick={() => handlePaymentSelect(methodKey)}
                    >
                      <div className={styles.methodHeader}>
                        <div className={styles.methodInfo}>
                          <div className={styles.methodIcon}>
                            <PaymentIcon methodKey={methodKey} />
                          </div>
                          <div className={styles.methodDetails}>
                            <div className={styles.methodNameRow}>
                              <span className={styles.methodName}>
                                {methodKey === 'upi' ? 'UPI' : methodKey === 'emi' ? 'EMI' : methodKey.charAt(0).toUpperCase() + methodKey.slice(1)}
                              </span>
                              {method.available_offers && method.available_offers.length > 0 && (
                                <span className={styles.offerBadge}>
                                  {method.available_offers.length} {method.available_offers.length === 1 ? 'offer' : 'offers'}
                                </span>
                              )}
                            </div>
                            {method.convenience_fee > 0 && (
                              <div className={styles.methodFee}>
                                <span className={styles.feeText}>
                                  {formatCurrency((order.totalItemsBuyPrice * method.convenience_fee) / 100)} convenience fee
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={styles.radioButton} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            checked={selectedPayment === methodKey}
                            onChange={() => handlePaymentSelect(methodKey)}
                            className={styles.radioInput}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className={styles.noMethods}>
                <span>No payment methods available</span>
              </div>
            )}
          </div>

          {/* Right Panel - Order Summary */}
          <div ref={orderSummaryRef} className={styles.orderSummaryPanel}>
            <div className={styles.summaryHeader}>
              <h2 className={styles.summaryTitle}>Order Summary</h2>
            </div>

            <div className={styles.summaryItems}>
              <div className={styles.listItemsPayment}>
                <div className={styles.listHeading}>
                  Items ({order.totalItems})
                </div>
                <div className={styles.allItems}>
                  {order.items.map((item, index) => (
                    <div key={index} className={styles.individualItem}>
                      <span className={styles.gameStore}>
                        <img src={item.offerFromStore.icon} alt="Store" />
                      </span>
                      <span className={styles.gameName}>
                        {item.title}
                      </span>
                      {moreThanOneQuantity && (
                        <span className={styles.totalUnits}>
                          x {item.quantity}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles['listItemsPayment'] + " is-align-items-center"}>
                <div className={styles.listHeading}>Total</div>
                <div className={styles.individualAmount}>
                  {order.totalItemsDisplayPriceString}
                </div>
              </div>
              {order.totalItemsDiscountValue > 0 && (
                <div className={styles['listItemsPayment'] + " is-align-items-center"}>
                  <div className={styles.offerApplied}>Offer applied</div>
                  <div className={styles.discountPrice}>
                    -{order.totalItemsDiscountValueString}
                  </div>
                </div>
              )}
              {selectedPayment && paymentMethods[selectedPayment] && paymentMethods[selectedPayment].convenience_fee > 0 && (
                <div className={styles['listItemsPayment'] + " is-align-items-center"}>
                  <div className={styles.convenienceFeeLabel}>Convenience Fee</div>
                  <div className={styles.convenienceFeeValue}>
                    +{formatCurrency(getConvenienceFee())}
                  </div>
                </div>
              )}
              <div className="is-flex is-justify-content-space-between is-align-items-center">
                {order.payOfferItems.map((item: string, index: number) => (
                  <div key={index} className={`${styles["items"]} fs-gray`}>
                    {item}
                  </div>
                ))}
              </div>
              
              <div className={styles['listItemsPayment'] + " is-align-items-center"}>
                <div className={styles.total}>Total Payable</div>
                <div className={styles.totalAmount}>
                  {order.totalItemsBuyPriceString}
                </div>
              </div>
            </div>

            {/* Available Offers Section */}
            {selectedPayment && paymentMethods[selectedPayment] && paymentMethods[selectedPayment].available_offers && paymentMethods[selectedPayment].available_offers.length > 0 && (
              <div className={styles.offersSection}>
                <div className={styles.sectionTitle}>Available Offers</div>
                <div className={styles.offersList}>
                  {paymentMethods[selectedPayment].available_offers.map((offer, index) => (
                    <div key={index} className={styles.offerItem}>
                      <div className={styles.offerIcon}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#FFD700" stroke="#FFD700" strokeWidth="1"/>
                        </svg>
                      </div>
                      <span className={styles.offerLabel}>{offer.lable}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedPayment && paymentMethods[selectedPayment]?.enforce_kyc && (
              <div className={styles.kycNoticeBanner} role="status">
                <div className={styles.kycNoticeIcon} aria-hidden>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 2v5h5M9 13h6M9 17h4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div className={styles.kycNoticeContent}>
                  <span className={styles.kycNoticeTitle}>KYC required for this method</span>
                  <span className={styles.kycNoticeDescription}>
                    You will be asked to verify your identity before payment can be completed.
                  </span>
                </div>
              </div>
            )}

            {/* Pay Button */}
            <div className={styles.payButtonContainer}>
              <button
                className={styles.payButton}
                disabled={!selectedPayment || isLoading}
                onClick={() => selectedPayment && handleProceed(selectedPayment)}
              >
                Pay {order.totalItemsBuyPriceString}
              </button>
            </div>
          </div>
        </div>
      </main>
      {
        isLoading &&
        <div className={styles.loaderOverlay}>
          <div className={styles.loaderContent}>
            <Loader size={60} color="#A83AFE" />
            <div className={styles.loadingMessage}>
              {loadingMessage}
            </div>
            <div className={styles.loadingSubtext}>
              Please do not close this window
            </div>
          </div>
        </div>
      }

      {/* Billing Address Modal */}
      <Modal
        isOpen={showBillingAddressModal}
        onClose={() => {
          setShowBillingAddressModal(false);
          setPendingPaymentMethod(null);
        }}
      >
        <div className={modalStyles.billingModal}>
          <div className={modalStyles.modalHeader}>
            <h2 className={modalStyles.modalTitle}>Billing Address Required</h2>
            <button
              className={modalStyles.closeButton}
              onClick={() => {
                setShowBillingAddressModal(false);
                setPendingPaymentMethod(null);
              }}
            >
              ×
            </button>
          </div>
          <div className={modalStyles.modalBody}>
            <div className={modalStyles.modalDescription}>
              <span>
                {authStore.user?.billingAddress && !isBillingAddressComplete()
                  ? "Please complete your billing address. Some required fields are missing."
                  : "We need your billing address to process your payment securely. This information is required before proceeding to the payment gateway."}
              </span>
            </div>
            <BillingAddressForm
              initialData={authStore.user?.billingAddress || null}
              onSubmit={handleBillingAddressSubmit}
              submitButtonText="Continue to Payment"
              showCancelButton={false}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default observer(Payment);

